import { useState } from 'react';
import { format } from 'date-fns';
import type { ApiResponse, MergedScheduledPayment } from 'vopay';
import { api } from '@/services/api.config';

export default function useEditScheduledTransactionAmount() {
  const [editing, setEditing] = useState(false);

  // /scheduled-payment/edit applies the amount to every remaining payment in one
  // call, but cannot reach the legacy VIOEftScheduledPayments table. Legacy
  // schedules have no bulk endpoint, so each remaining payment is edited through
  // the EFT per-payment endpoint, keyed by its own date.
  async function editLegacy(transaction: MergedScheduledPayment, amount: number): Promise<ApiResponse> {
    // UpcomingPayment is validated as strictly after today (VIOMultiDateValidator
    // with OnlyFutureDate), so past-dated rows — which linger when the scheduler
    // has not advanced them — cannot be edited and are skipped.
    const today = format(new Date(), 'yyyy-MM-dd');
    const editablePayments = transaction.UpcomingTransactions.filter((payment) => payment.ScheduledDate > today);

    if (editablePayments.length === 0) {
      throw new Error('This schedule has no remaining payments that can still be edited.');
    }

    const edit = transaction.PaymentType === 'debit' ? api.postEditScheduledFund : api.postEditScheduledWithdraw;

    const results = await Promise.all(
      editablePayments.map((payment) =>
        edit({
          ScheduledTransactionID: transaction.ID,
          UpcomingPayment: payment.ScheduledDate,
          // The legacy EFT requests still take the amount as a string
          Amount: String(amount),
        }),
      ),
    );

    const failed = results.find((result) => !result.Success);

    if (failed) {
      throw new Error(failed.ErrorMessage);
    }

    return results[0];
  }

  async function editScheduledTransactionAmount(
    transaction: MergedScheduledPayment,
    amount: number,
  ): Promise<ApiResponse> {
    try {
      setEditing(true);

      if (transaction.IsLegacy) {
        return await editLegacy(transaction, amount);
      }

      const res = await api.editScheduledPayment({ ScheduledPaymentsID: transaction.ID, Amount: amount });

      if (!res.Success) {
        throw new Error(res.ErrorMessage);
      }

      return res;
    } catch (error) {
      console.error('Edit scheduled transaction amount failed:', error);
      throw error;
    } finally {
      setEditing(false);
    }
  }

  return { editing, editScheduledTransactionAmount };
}
